// The building's OWN grid, drawn in 3D - the named lines a person on
// site actually uses ("meet me at C/4"), not a lattice.
//
// WHAT WAS THERE BEFORE. onShowGrid built a THREE.GridHelper sized from
// the model footprint: an anonymous ground grid with no named lines and
// nothing to label. That is why the owner saw no labels - there was
// nothing to put on them. He asked for the exported Revit grid on the
// same G button instead, labelled at both ends "like navisworks".
//
// THE SOURCE IS <Doc>_Grids.json, NOT manifest.grid. The manifest route
// (label -> feet, plus modelFrame) needs a transform, and BA03 - the
// project he is looking at - has NO modelFrame in its published
// manifest, so that route cannot work there at all. The exporter's side
// file carries the lines as points in the export frame with the link
// transform already applied, which is the frame the rest of the export
// is measured in.
//
// === THE ONE THING THAT IS NOT OBVIOUS: THE UP AXIS ===
//
// "Same frame as the geometry" is true of units, origin and link
// transform, and NOT true of the up axis once it reaches the viewer:
//
//   - the C# pipeline passes per-instance matrices through
//     MatrixZUpToYUp before writing them (loader/streaming/
//     instanceMaterializer.js, MATRIX CONVENTION), and for a Revit
//     export the loader's root matrix is identity with manifest bounds
//     "already in viewer frame" (streamingLoader.js ~663). So the
//     GEOMETRY the viewer draws is Y-up.
//   - <Doc>_Grids.json is a sibling of <Doc>_Rooms.json and is written
//     in the EXPORT frame, which is Z-up: its points are 2D [x, y] and
//     BOTH are horizontal. back/utils/glance/gridFit.js matches them
//     against manifest.grid in plan feet, which only makes sense that
//     way.
//
// So a grid point is NOT (gx, gy, 0) in the viewer. Drawing it raw puts
// the whole grid in a VERTICAL plane. The mapping lives here, once, in
// gridPointToViewer, because getting it wrong is the failure that looks
// plausible in a screenshot and is wrong on site - a mirrored grid
// still renders a neat rectangle with correct-looking labels.
//
// PLAN_FLIP is the sign of that conversion and is deliberately a named
// constant: Z-up (x, y, z) -> Y-up (x, z, -y) is the conventional axis
// swap, so plan +y becomes viewer -z. Confirmation requested from
// root-04, who fits these same grids against the building on prod.
// If it turns out to be +1, this is the only line that changes.
const PLAN_FLIP = -1;

/**
 * One point of a grid line, from the export's plan frame into the
 * viewer's Y-up frame at a given elevation.
 */
export function gridPointToViewer(gx, gy, elevation) {
  return { x: Number(gx), y: Number(elevation), z: PLAN_FLIP * Number(gy) };
}

// A line we can actually draw: two finite ends, far enough apart to have
// a direction. Curved grids are skipped rather than drawn as a chord -
// a straight line through a curved grid's ends is not that grid, and a
// wrong line is worse than a missing one. gridFit.segmentOf makes the
// same call for the same reason.
export function segmentOfLine(g) {
  if (!g || g.isCurved) return null;
  const pts = g.points;
  if (!Array.isArray(pts) || pts.length < 2) return null;
  const a = pts[0];
  const b = pts[pts.length - 1];
  if (!Array.isArray(a) || !Array.isArray(b)) return null;
  const vals = [a[0], a[1], b[0], b[1]].map(Number);
  if (!vals.every(Number.isFinite)) return null;
  if (Math.hypot(vals[2] - vals[0], vals[3] - vals[1]) <= 1) return null;
  const label = g.label != null ? g.label : g.name;
  return {
    label: String(label == null ? '' : label).trim(),
    a: [vals[0], vals[1]],
    b: [vals[2], vals[3]],
  };
}

/**
 * WHICH DOCUMENT'S GRID TO DRAW when a project has many.
 *
 * BA03 publishes 1,032 grid lines across 17 documents and they are NOT
 * one grid: BA_03_SOLAR_MD_B3D reuses the same labels about 107 ft away,
 * so pooling them draws two grids on top of each other, both labelled
 * "A", and the owner cannot tell which is the building's.
 *
 * So: one document wins, whole. `preferredDoc` is the source document
 * root-04's selector chose - gridFit.sourceDocument, which counts
 * Copy/Monitor coincidence rather than labels.
 *
 * WHEN WE DO NOT KNOW, WE DO NOT GUESS. The obvious fallback is "the
 * document with the most lines", and it is WRONG on the project the
 * owner is looking at: on BA03 it picks STARLINK_BASTROP_BA03-DSI-MECH
 * (86 lines) over BA04 Core Shell (83), and DSI-MECH carries D, E and F
 * - three labelled lines that are NOT in the building grid (root-04,
 * 2026-09-22, who also found that the two coincide to 0.00 in, so this
 * changes nothing for placement and everything for a drawn label).
 * Somebody reads a grid label off the screen and walks to it, so three
 * extra lines is not a cosmetic difference.
 *
 * A wrong-document grid is the same failure as the anonymous lattice
 * presented as the building's: plausible, labelled, and wrong. So with
 * more than one candidate and no answer we report `ambiguous` and the
 * caller shows the reference grid instead. One candidate is not a guess
 * and is taken. The choice is REPORTED in every branch, because "which
 * grid am I looking at" is the question this feature exists to answer.
 */
export function selectGridSource(byDocument, preferredDoc) {
  const usable = [];
  for (const entry of byDocument || []) {
    if (!entry) continue;
    const lines = (entry.grids || []).map(segmentOfLine).filter(Boolean);
    if (lines.length) usable.push({ name: entry.name, lines });
  }
  if (!usable.length) return { lines: [], name: null, reason: 'no-grid' };

  const want = String(preferredDoc == null ? '' : preferredDoc).trim().toUpperCase();
  const preferred = want
    ? usable.find((d) => String(d.name || '').trim().toUpperCase() === want)
    : null;
  if (preferred) return { lines: preferred.lines, name: preferred.name, reason: 'source-document' };

  if (usable.length === 1) {
    return { lines: usable[0].lines, name: usable[0].name, reason: 'only-document' };
  }
  return { lines: [], name: null, reason: 'ambiguous', candidates: usable.length };
}

// Labels sit at BOTH ends of every line - that is what the owner meant
// by "the 4 corners": ends along all four edges of the grid rectangle,
// the way Navisworks shows it, not one label per line.
//
// The cap is not decoration. CSS2DRenderer.render walks the whole scene
// graph every frame it runs (viewer/index.js notes this against a 7k
// pool on SpaceX), and every label is a DOM node. One document's grid is
// tens of lines; a pooled or pathological export could be thousands, and
// the honest failure there is to draw the lines and say the labels were
// too many - not to make the viewer crawl.
export const MAX_LABELLED_LINES = 400;

export function buildGridDraw(lines, elevation) {
  const positions = [];
  const labels = [];
  // One entry per labelled LINE, for the label overlay (./gridLabels),
  // which places both of a line's labels itself, frame by frame.
  const labelLines = [];
  const labelled = (lines || []).length <= MAX_LABELLED_LINES;
  for (const line of lines || []) {
    const a = gridPointToViewer(line.a[0], line.a[1], elevation);
    const b = gridPointToViewer(line.b[0], line.b[1], elevation);
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
    if (labelled && line.label) {
      labels.push({ text: line.label, at: a });
      labels.push({ text: line.label, at: b });
      labelLines.push({ text: line.label, a, b });
    }
  }
  return {
    positions,
    labels,
    labelLines,
    lineCount: (lines || []).length,
    labelsSuppressed: !labelled,
  };
}

// HOW CONFIDENT IS THE CHOICE, from root-04's `how` (the grid-source
// route: monitored | host | most-lines | no-grids).
//
// The route always returns its best candidate, and its LAST resort is
// "the document with the most lines" - the exact rule that picks
// DSI-MECH on BA03. Centrally that is right: it is a considered last
// resort with more evidence than the viewer has. But the viewer must
// not present it with the same confidence as a grid proven by
// Copy/Monitor or by matching the building grid, or we are back to a
// guess wearing the answer's clothes.
//
// 'monitored' means the document others Copy/Monitor - that IS the
// building grid. 'host' and 'most-lines' are positional: they say which
// document we would bet on, not which grid is the building's.
export function gridConfidence(how) {
  return how === 'monitored' ? 'known' : 'guess';
}
